/**
 * Push topics the user can switch on and off.
 *
 * The backend also knows language and platform topics (`de`, `android`, …). Those are
 * derived from `language` and the runtime platform when subscribing, so they are not
 * settings and must not be listed here.
 */
export enum IPushChannel {
  GENERAL = 'general',
  SPORTS = 'sports',
  LIBRARY = 'library',
  HEALTH_PASSPORT = 'healthPassport',
}

export enum ILanguage {
  DE = 'de-DE',
  EN = 'en-US',
}

export enum IWeatherStyle {
  GREEN = 'green',
  WHITE = 'white',
  COLORED = 'colored',
}

export enum IWeatherWindUnit {
  M_S = 'm_s',
  KM_H = 'km_h',
  KTS = 'kts',
  BFT = 'bft',
}

export interface PushState {
  enabled: boolean;
  channel: { [K in IPushChannel]: boolean };
}

/** The user fills these in themselves, so every field can legitimately be absent. */
export interface PersonalContactDetails {
  surname?: string;
  givenname?: string;
  dateOfBirth?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  telephone?: string;
}

export interface HomescreenSetting {
  position: number | null;
}

export type HomescreenSettings = Record<string, HomescreenSetting>;

export interface SettingsState {
  /**
   * Remembered selections are held as ids, not as whole domain objects: this state is
   * persisted across releases, and a stored copy of an object would go stale as soon as
   * its interface changes. Resolve the id against the respective service when reading.
   */
  canteen: {
    remember: boolean;
    rememberedId: string | null;
  };
  departureStation: {
    remember: boolean;
    rememberedId: string | null;
  };
  campusNavi: {
    rememberedCategories: number[];
  };
  homescreen: HomescreenSettings;
  language: ILanguage;
  personalContactDetails: PersonalContactDetails;
  push: PushState;
  weather: {
    enabled: boolean;
    style: IWeatherStyle;
    windUnit: IWeatherWindUnit;
  };
}

export const initialSettingsState: SettingsState = {
  canteen: {
    remember: true,
    rememberedId: null,
  },
  departureStation: {
    remember: true,
    rememberedId: null,
  },
  campusNavi: {
    rememberedCategories: [0, 1, 2, 3, 4, 6, 7, 8, 9, 10, 11],
  },
  // Empty until `reconcileHomescreen` fills it from the tile catalogue and the user's
  // roles, which are not known yet at this point.
  homescreen: {},
  language: ILanguage.DE,
  personalContactDetails: {},
  push: {
    enabled: false,
    channel: {
      [IPushChannel.GENERAL]: true,
      [IPushChannel.SPORTS]: true,
      [IPushChannel.LIBRARY]: true,
      [IPushChannel.HEALTH_PASSPORT]: true,
    },
  },
  weather: {
    enabled: true,
    style: IWeatherStyle.COLORED,
    windUnit: IWeatherWindUnit.KM_H,
  },
};
